import { ReactNode } from 'react'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'

/** Fixed set of context types for lookup filter field styling (form vs table vs parent/child). */
export const LookupFilterContextType = {
  FORM: 'form',
  CURRENT_TABLE: 'currentTable',
  PARENT_TABLE: 'parentTable',
  CHILD_TABLE: 'childTable',
} as const

export type LookupFilterContextType =
  (typeof LookupFilterContextType)[keyof typeof LookupFilterContextType]

/** Maps each context type to its display className (color coding). */
export const LOOKUP_FILTER_CONTEXT_CLASS_NAME: Record<LookupFilterContextType, string> = {
  [LookupFilterContextType.FORM]: 'text-purple-8 bg-purple-2 rounded-sm px-1 py-0.5 font-semibold',
  [LookupFilterContextType.CURRENT_TABLE]:
    'text-blue-8 bg-blue-2 rounded-sm px-1 py-0.5 font-semibold',
  [LookupFilterContextType.PARENT_TABLE]:
    'text-green-9 bg-green-3 rounded-sm px-1 py-0.5 font-semibold',
  [LookupFilterContextType.CHILD_TABLE]:
    'text-orange-9 bg-orange-3 rounded-sm px-1 py-0.5 font-semibold',
}

const DEFAULT_FIELD_CLASS_NAME =
  LOOKUP_FILTER_CONTEXT_CLASS_NAME[LookupFilterContextType.CURRENT_TABLE]

export interface FormatLogicExpressionForDisplayOptions {
  fieldClassName?: string
  /** Field id → tooltip text (e.g. "Form input", "Table: Orders"). */
  fieldContextNameMap?: Record<string, string> | null
  /** Field id → context type for color coding. When provided, class is taken from LOOKUP_FILTER_CONTEXT_CLASS_NAME. */
  fieldContextTypeMap?: Record<string, LookupFilterContextType> | null
}

/**
 * Turns a logic expression string into an array of React nodes suitable for display.
 *
 * - Replaces field references (e.g. `{fieldId}` or `fieldId`) with the display name from
 *   fieldNameMap, wrapped in a styled span.
 * - Optional braces in the string are matched by the regex (`\{?…\}?`); both `{id}` and `id` work.
 * - When fieldContextNameMap is provided, each field is wrapped in a Tooltip (e.g. "Form input",
 *   "Table: Orders").
 * - Styling: when fieldContextTypeMap is provided, each field's class is taken from
 *   LOOKUP_FILTER_CONTEXT_CLASS_NAME[type]; otherwise fieldClassName is used.
 */
export function formatLogicExpressionForDisplay(
  logicExpressionString: string,
  fieldNameMap: Record<string, string>,
  options: FormatLogicExpressionForDisplayOptions = {},
): ReactNode[] {
  const {
    fieldClassName = DEFAULT_FIELD_CLASS_NAME,
    fieldContextNameMap,
    fieldContextTypeMap,
  } = options
  const fieldIds = Object.keys(fieldNameMap)
  if (fieldIds.length === 0) {
    return [logicExpressionString]
  }

  // Match optional braces plus any known field id: e.g. "{abc}" or "abc"
  const regex = new RegExp(`\\{?(${fieldIds.join('|')})\\}?`, 'g')
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match

  while ((match = regex.exec(logicExpressionString)) !== null) {
    // Push any literal text before this match
    if (match.index > lastIndex) {
      parts.push(logicExpressionString.substring(lastIndex, match.index))
    }
    const fieldId = match[1]
    const label = fieldNameMap[fieldId]
    const tooltipText = fieldContextNameMap?.[fieldId]
    const key = `${fieldId}-${match.index}`
    const contextType = fieldContextTypeMap?.[fieldId]
    const resolvedClassName = contextType
      ? LOOKUP_FILTER_CONTEXT_CLASS_NAME[contextType]
      : fieldClassName

    parts.push(
      tooltipText ? (
        <Tooltip key={key} title={tooltipText}>
          <span className={resolvedClassName}>{label}</span>
        </Tooltip>
      ) : (
        <span key={key} className={resolvedClassName}>
          {label}
        </span>
      ),
    )
    lastIndex = match.index + match[0].length
  }

  // Push any remaining literal text after the last match
  if (lastIndex < logicExpressionString.length) {
    parts.push(logicExpressionString.substring(lastIndex))
  }

  return parts
}
